"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { resolveApiUrl } from "@/data/runtime-data";

type HoursRow = { label: string; value: string };
type ContactData = {
  phone: string;
  phoneHref: string;
  email: string;
  addressLine1: string;
  addressLine2?: string;
  mapLink: string;
  kitchenCloseDisplay?: string;
};

type Props = {
  initialContact: ContactData;
  initialHours: HoursRow[];
};

export function ContactDetails({ initialContact, initialHours }: Props) {
  const [contact, setContact] = useState<ContactData>(initialContact);
  const [hours, setHours] = useState<HoursRow[]>(initialHours);

  useEffect(() => {
    const load = async () => {
      try {
        const [contactRes, hoursRes] = await Promise.all([
          fetch(resolveApiUrl("/api/contact"), { cache: "no-store" }),
          fetch(resolveApiUrl("/api/hours"), { cache: "no-store" }),
        ]);

        const contactJson = contactRes.ok
          ? ((await contactRes.json()) as {
              phoneDisplay?: string;
              phoneHref?: string;
              email?: string;
              addressLine1?: string;
              addressLine2?: string;
            })
          : null;
        if (contactJson) {
          setContact((prev) => ({
            ...prev,
            phone: contactJson.phoneDisplay ?? prev.phone,
            phoneHref: contactJson.phoneHref ?? prev.phoneHref,
            email: contactJson.email ?? prev.email,
            addressLine1: contactJson.addressLine1 ?? prev.addressLine1,
            addressLine2: contactJson.addressLine2 ?? prev.addressLine2,
          }));
        }

        const hoursJson = hoursRes.ok
          ? ((await hoursRes.json()) as { weeklyHours?: any[]; kitchenCloseTime?: string })
          : null;
        if (hoursJson?.weeklyHours) {
          setHours(hoursJson.weeklyHours.map((row: any) => ({ label: row.label, value: row.value })));
          const kitchen = hoursJson.kitchenCloseTime;
          if (typeof kitchen === "string") {
            setContact((prev) => ({
              ...prev,
              kitchenCloseDisplay: formatKitchen(kitchen),
            }));
          }
        }
      } catch (error) {
        console.warn("Failed to refresh contact details", error);
      }
    };
    void load();
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="card bg-white">
        <h2 className="text-sm uppercase tracking-widest text-gray-500 mb-2">Call</h2>
        <Link href={contact.phoneHref} className="text-2xl font-semibold text-gray-900 hover:text-black break-words">
          {contact.phone}
        </Link>
        <p className="mt-3 text-sm text-gray-600">Call to place takeout orders, check availability, or ask about specials.</p>
      </div>

      <div className="card bg-white">
        <h2 className="text-sm uppercase tracking-widest text-gray-500 mb-2">Email</h2>
        <Link href={`mailto:${contact.email}`} className="text-lg font-semibold text-gray-900 hover:text-black break-words">
          {contact.email}
        </Link>
        <p className="mt-3 text-sm text-gray-600">Send questions about dietary preferences, group visits, or menu details.</p>
      </div>

      <div className="card bg-white">
        <h2 className="text-sm uppercase tracking-widest text-gray-500 mb-2">Find Us</h2>
        <p className="text-gray-800 font-semibold">
          {contact.addressLine1}
          <br />
          {contact.addressLine2}
        </p>
        <Link
          href={contact.mapLink}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-black hover:underline"
        >
          View in Google Maps
        </Link>
      </div>

      <div className="card bg-gray-50 md:col-span-2">
        <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-2">Hours</h3>
        <ul className="space-y-2 text-gray-800 break-words">
          {hours.map((entry) => (
            <li key={entry.label} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
              <span className="flex-1">{entry.label}</span>
              <span className="font-semibold text-right whitespace-normal">{entry.value}</span>
            </li>
          ))}
        </ul>
        {contact.kitchenCloseDisplay && (
          <p className="mt-3 text-sm text-gray-600">Kitchen closes at {contact.kitchenCloseDisplay}.</p>
        )}
      </div>
    </div>
  );
}

const formatKitchen = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const normalizedHour = hours % 12 || 12;
  return `${normalizedHour}:${minutes.toString().padStart(2, "0")} ${period}`;
};
